export const FETCH_CACHE_TAG = {
  BUILDER_COMMON: "builder-fetch-cache-tag",
  MAGENTO_COMMON: "magento-fetch-cache-tag",
} as const;

export type FetchCacheTag = string;

const ROUTE_SCOPED_BUILDER_MODELS = [
  "blog-listing-section-model",
  "blog-single-article-section-model",
  "loyalty-blog-listing-section-model",
  "loyalty-blog-single-article-section-model",
];

const LOCALE_SCOPED_BUILDER_MODELS = ["store-locator"];

const PAGE_BUILDER_MODEL = "page";

// also used for the dynamic-slug `page` model: the webhook revalidation and the
// page render both feed the same normalized locale + urlPath, so the tags match.
export const createBuilderRouteFetchCacheTag = ({ locale, urlPath }: { locale: string; urlPath: string }) => {
  const normalizedPath = normalizeTagSegment(urlPath) || "root";
  return `builder-${normalizeTagSegment(locale)}-${normalizedPath}-fetch-cache-tag`;
};

// breadcrumbs use ancestor pages (including "/") on every route, so they can't reuse the route
// tag - otherwise publishing one page invalidates every page that lists it in its breadcrumbs
export const createBuilderBreadcrumbFetchCacheTag = ({ locale, urlPath }: { locale: string; urlPath: string }) => {
  const normalizedPath = normalizeTagSegment(urlPath) || "root";
  return `builder-${normalizeTagSegment(locale)}-${normalizedPath}-breadcrumb-fetch-cache-tag`;
};

// creates cache tag for seo store-locator using only the root path (not nested routes).
// all routes under nested-store-locator (including /nested-store-locator/province/city/store)
// share the same tag for invalidation
export const createBuilderStoreLocatorFetchCacheTag = ({
  locale,
  rewrittenRootPath,
}: {
  locale: string;
  rewrittenRootPath: string;
}) => {
  const rootOnly = rewrittenRootPath.split("/")[1] || rewrittenRootPath;
  return `builder-${normalizeTagSegment(locale)}-${normalizeTagSegment(rootOnly)}-fetch-cache-tag`;
};

export const isRouteScopedBuilderModel = (modelName: string) => {
  return ROUTE_SCOPED_BUILDER_MODELS.includes(modelName);
};

export const isLocaleScopedBuilderModel = (modelName: string) => {
  return LOCALE_SCOPED_BUILDER_MODELS.includes(modelName);
};

export const isPageBuilderModel = (modelName: string) => {
  return modelName === PAGE_BUILDER_MODEL;
};

export const createBuilderFetchCacheTags = ({
  modelName,
  locale,
  urlPath,
}: {
  modelName: string;
  locale?: string;
  urlPath?: string;
}): FetchCacheTag[] => {
  if (isLocaleScopedBuilderModel(modelName)) {
    const rootPath = urlPath ? `/${urlPath.split("/").at(1)}` : "nested-store-locator";

    return locale
      ? [createBuilderStoreLocatorFetchCacheTag({ locale, rewrittenRootPath: rootPath })]
      : [FETCH_CACHE_TAG.BUILDER_COMMON];
  }

  if (!isRouteScopedBuilderModel(modelName) && !isPageBuilderModel(modelName)) {
    return [FETCH_CACHE_TAG.BUILDER_COMMON];
  }

  if (locale && urlPath) {
    return [createBuilderRouteFetchCacheTag({ locale, urlPath })];
  }

  return [FETCH_CACHE_TAG.BUILDER_COMMON];
};

const normalizeTagSegment = (value: string) => {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, "")
    .replace(/[^a-z0-9/.-]+/g, "-")
    .replace(/\//g, "-")
    .replace(/-+/g, "-");

  return normalized;
};
